import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import React, { useState } from 'react';
import { Drawer, Tabs, Button, Space, Card } from 'antd';
import { FileText, Network, ListTodo, FileCode, X, Copy, Check, Download } from 'lucide-react';
import { MermaidSnippet } from './snippets/MermaidSnippet.js';
import { AgentTodoSnippet, type TodoItem } from './snippets/AgentTodoSnippet.js';
import { BoundedMarkdownRenderer } from './BoundedMarkdownRenderer.js';
import { downloadArtifactFile } from './snippets/ArtifactSnippet.js';
import { useTranslation } from '@govard/i18n-core/react';

export type ArtifactType = 'document' | 'mermaid' | 'todo' | 'html';

export interface ArtifactContent {
  id: string;
  type: ArtifactType;
  title: string;
  subtitle?: string;
  content: string;
  todoItems?: TodoItem[];
}

export interface ArtifactCanvasProps {
  open: boolean;
  artifact: ArtifactContent | null;
  onClose: () => void;
}

export const ArtifactCanvas: React.FC<ArtifactCanvasProps> = ({ open, artifact, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>('preview');
  const [copied, setCopied] = useState(false);

  if (!artifact) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(artifact.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleExport = () => {
    downloadArtifactFile(artifact.title, artifact.content, artifact.type);
  };

  const getIcon = () => {
    switch (artifact.type) {
      case 'mermaid':
        return <Network size={18} style={{ color: 'var(--ant-color-info)' }} />;
      case 'todo':
        return <ListTodo size={18} style={{ color: 'var(--ant-color-primary)' }} />;
      case 'html':
        return <FileCode size={18} style={{ color: 'var(--ant-color-success)' }} />;
      default:
        return <FileText size={18} style={{ color: 'var(--ant-color-info)' }} />;
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size={560}
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space size={8}>
            {getIcon()}
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-text)' }}>
                {artifact.title}
              </div>
              {artifact.subtitle && (
                <div style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)' }}>
                  {artifact.subtitle}
                </div>
              )}
            </div>
          </Space>
          <Space size={4}>
            <EntityStatusChip tone="default" style={{ fontSize: 10, margin: 0 }}>
              {t('crm.ai_chat.artifact.type_label', { type: artifact.type.toUpperCase() })}
            </EntityStatusChip>
            <Button
              type="text"
              size="small"
              aria-label={t('crm.ai_chat.artifact.close_canvas')}
              icon={<X size={16} />}
              onClick={onClose}
            />
          </Space>
        </div>
      }
      closeIcon={false}
      styles={{
        header: {
          padding: '12px 16px',
          background: 'var(--g-fill)',
          borderBottom: '1px solid var(--ant-color-border)',
        },
        body: { padding: 16, background: 'var(--ant-color-bg-container)' },
      }}
      extra={
        <Space size={8}>
          <Button
            size="small"
            icon={
              copied ? (
                <Check size={13} style={{ color: 'var(--ant-color-success)' }} />
              ) : (
                <Copy size={13} />
              )
            }
            onClick={handleCopy}
          >
            {copied ? t('crm.ai_chat.artifact.copied') : t('crm.ai_chat.artifact.copy')}
          </Button>
          <Button size="small" icon={<Download size={13} />} onClick={handleExport}>
            {t('crm.ai_chat.artifact.export')}
          </Button>
        </Space>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="small"
        items={[
          {
            key: 'preview',
            label: t('crm.ai_chat.artifact.preview'),
            children: (
              <div style={{ marginTop: 8 }}>
                {artifact.type === 'mermaid' && (
                  <MermaidSnippet code={artifact.content} title={artifact.title} />
                )}

                {artifact.type === 'todo' && artifact.todoItems && (
                  <AgentTodoSnippet title={artifact.title} items={artifact.todoItems} />
                )}

                {artifact.type === 'document' && (
                  <Card
                    size="small"
                    style={{ borderRadius: 8, borderColor: 'var(--ant-color-border)' }}
                  >
                    <BoundedMarkdownRenderer content={artifact.content} isStreaming={false} />
                  </Card>
                )}

                {artifact.type === 'html' && (
                  <Card
                    size="small"
                    style={{ borderRadius: 8, borderColor: 'var(--ant-color-border)', padding: 0 }}
                  >
                    <iframe
                      title={t('crm.ai_chat.artifact.html_preview')}
                      srcDoc={artifact.content}
                      sandbox="allow-scripts"
                      style={{ width: '100%', height: 400, border: 'none', borderRadius: 6 }}
                    />
                  </Card>
                )}
              </div>
            ),
          },
          {
            key: 'code',
            label: t('crm.ai_chat.artifact.source'),
            children: (
              <pre
                style={{
                  background: 'var(--color-text)',
                  color: 'var(--g-fill)',
                  padding: 12,
                  borderRadius: 8,
                  fontSize: 12,
                  overflowX: 'auto',
                  fontFamily: 'monospace',
                }}
              >
                {artifact.content}
              </pre>
            ),
          },
        ]}
      />
    </Drawer>
  );
};
